import { useMemo } from 'react'

import {
  type F0FormSubmitResult,
  type F0WizardFormPerSectionSubmitArg,
  type F0WizardFormStep,
  f0FormField,
  useF0FormDefinition,
} from '@factorialco/f0-react'
import type { BenefitsHealthcareEnrollmentPlanFragment } from 'generated/resources/graphql'
import { z } from 'zod'

import i18n from 'modules/core/lib/i18n'
import { i18nDateToString, stringToDate } from 'modules/core/lib/dates'

import { WizardStepHeader } from './components/WizardStepHeader'
import { PlanOverviewContent } from './PlanOverviewStep'
import type {
  BenefitMemberEnrollmentData,
  IdentifierType,
  PersonalInformationFormData,
} from './types'
import {
  hasMissingEmployeeAddressData,
  normalizeHiddenEmployeeAddressFields,
  personalInformationFieldSchemas,
} from './validation'

const NAME_ROW_ID = 'name-row'
const PERSONAL_ROW_ID = 'personal-row'
const IDENTIFIER_ROW_ID = 'identifier-row'
const CONTACT_ROW_ID = 'contact-row'
const EMPLOYEE_MEMBER_TYPE = 'employee' as const

const buildPlanOverviewSchema = (healthcarePlanData: BenefitsHealthcareEnrollmentPlanFragment) =>
  z.object({
    providerName: f0FormField(z.string(), {
      label: '',
      fieldType: 'custom',
      render: () => <PlanOverviewContent healthcarePlanData={healthcarePlanData} />,
    }),
  })

const buildPersonalInformationSchema = (
  identifierTypes: IdentifierType[],
  showEmployeeAddressFields: boolean
) => {
  const fieldSchemas = personalInformationFieldSchemas(showEmployeeAddressFields)

  return z.object({
    stepHeader: f0FormField(z.string().optional(), {
      label: '',
      fieldType: 'custom',
      render: () => (
        <WizardStepHeader
          title={i18n.t('benefits.healthcare.enrollment_wizard.personal_information_step.title')}
          description={i18n.t(
            'benefits.healthcare.enrollment_wizard.personal_information_step.description'
          )}
        />
      ),
    }),
    firstName: f0FormField(fieldSchemas.firstName, {
      label: i18n.t('benefits.healthcare.member_data.name.label'),
      row: NAME_ROW_ID,
    }),
    lastName: f0FormField(fieldSchemas.lastName, {
      label: i18n.t('benefits.healthcare.member_data.surname.label'),
      row: NAME_ROW_ID,
    }),
    sex: f0FormField(fieldSchemas.sex, {
      label: i18n.t('benefits.healthcare.member_data.sex.label'),
      fieldType: 'select',
      options: [
        { label: i18n.t('common.male'), value: 'male' },
        { label: i18n.t('common.female'), value: 'female' },
      ],
      placeholder: i18n.t('common.select'),
      row: PERSONAL_ROW_ID,
    }),
    birthday: f0FormField(
      z
        .date({
          required_error: i18n.t('benefits.healthcare.plan_wizard.validation.common.required'),
        })
        .refine(
          (date) => date <= new Date(),
          i18n.t('benefits.healthcare.enrollment_wizard.validation.common.date_not_in_past')
        ),
      {
        label: i18n.t('benefits.healthcare.member_data.birthday.label'),
        fieldType: 'date',
        row: PERSONAL_ROW_ID,
        granularities: ['day'],
      }
    ),
    addressLine1: f0FormField(fieldSchemas.addressLine1, {
      label: i18n.t('benefits.healthcare.member_data.address.label'),
    }),
    addressLine2: f0FormField(fieldSchemas.addressLine2, {
      label: i18n.t('benefits.healthcare.member_data.address_line_2.label'),
    }),
    postalCode: f0FormField(fieldSchemas.postalCode, {
      label: i18n.t('benefits.healthcare.member_data.postal_code.label'),
      renderIf: () => showEmployeeAddressFields,
    }),
    city: f0FormField(fieldSchemas.city, {
      label: i18n.t('benefits.healthcare.member_data.city.label'),
      renderIf: () => showEmployeeAddressFields,
    }),
    province: f0FormField(fieldSchemas.province, {
      label: i18n.t('benefits.healthcare.member_data.province.label'),
      renderIf: () => showEmployeeAddressFields,
    }),
    taxIdType: f0FormField(fieldSchemas.taxIdType, {
      label: i18n.t('benefits.healthcare.member_data.identifier_type.label'),
      fieldType: 'select',
      options: identifierTypes,
      placeholder: i18n.t('common.select'),
      row: IDENTIFIER_ROW_ID,
    }),
    taxIdNumber: f0FormField(fieldSchemas.taxIdNumber, {
      label: i18n.t('benefits.healthcare.member_data.identifier_number.label'),
      row: IDENTIFIER_ROW_ID,
    }),
    email: f0FormField(fieldSchemas.email, {
      label: i18n.t('common.email'),
      row: CONTACT_ROW_ID,
    }),
    phoneNumber: f0FormField(fieldSchemas.phoneNumber, {
      label: i18n.t('benefits.healthcare.member_data.phone.label'),
      placeholder: '+34695858423',
      row: CONTACT_ROW_ID,
    }),
    iban: f0FormField(fieldSchemas.iban, {
      label: i18n.t('benefits.healthcare.member_data.iban.label'),
      placeholder: i18n.t('benefits.healthcare.member_data.iban.placeholder'),
    }),
    memberType: f0FormField(fieldSchemas.memberType, {
      label: '',
      renderIf: () => false,
    }),
  })
}

type HealthcareEnrollmentWizardSchema = {
  planOverview: ReturnType<typeof buildPlanOverviewSchema>
  personalInformation: ReturnType<typeof buildPersonalInformationSchema>
}

interface UseHealthcareEnrollmentWizardFormParams {
  healthcarePlanData: BenefitsHealthcareEnrollmentPlanFragment
  identifierTypes: IdentifierType[]
  initialPersonalInformationData: PersonalInformationFormData
  onSubmit: (data: BenefitMemberEnrollmentData) => void | Promise<void>
}

const toDateOrUndefined = (value?: string) =>
  value ? stringToDate(value, 'backend').toDate() : undefined

const toSexValue = (value?: string) => {
  if (value === 'male') return 'male' as const
  if (value === 'female') return 'female' as const

  return undefined
}

const mapDefaultPersonalInformationValues = (data: PersonalInformationFormData) => ({
  firstName: data.firstName ?? '',
  lastName: data.lastName ?? '',
  sex: toSexValue(data.sex),
  birthday: toDateOrUndefined(data.birthday),
  addressLine1: data.addressLine1 ?? '',
  addressLine2: data.addressLine2 ?? '',
  postalCode: data.postalCode ?? '',
  city: data.city ?? '',
  province: data.province ?? '',
  taxIdType: data.taxIdType ?? undefined,
  taxIdNumber: data.taxIdNumber ?? '',
  email: data.email ?? '',
  phoneNumber: data.phoneNumber ?? '',
  iban: data.iban ?? '',
  memberType: EMPLOYEE_MEMBER_TYPE,
})

type PersonalInformationFormValues = z.infer<ReturnType<typeof buildPersonalInformationSchema>>

const buildSubmitPayload = (
  submissionValues: PersonalInformationFormValues,
  showEmployeeAddressFields: boolean
): BenefitMemberEnrollmentData => {
  const normalizedValues = normalizeHiddenEmployeeAddressFields({
    ...submissionValues,
    postalCode: showEmployeeAddressFields ? submissionValues.postalCode : '',
    city: showEmployeeAddressFields ? submissionValues.city : '',
    province: showEmployeeAddressFields ? submissionValues.province : '',
  })

  return {
    ...normalizedValues,
    memberType: EMPLOYEE_MEMBER_TYPE,
    birthday: i18nDateToString(normalizedValues.birthday, 'backend'),
    addressLine2: normalizedValues.addressLine2 || '',
    postalCode: normalizedValues.postalCode || '',
    city: normalizedValues.city || '',
    province: normalizedValues.province || '',
  }
}

const mapSubmissionData = (
  values: PersonalInformationFormValues,
  showEmployeeAddressFields: boolean
) : BenefitMemberEnrollmentData => {
  const { stepHeader: _stepHeader, ...submissionValues } = values

  return buildSubmitPayload(submissionValues, showEmployeeAddressFields)
}

export const useHealthcareEnrollmentWizardForm = ({
  healthcarePlanData,
  identifierTypes,
  initialPersonalInformationData,
  onSubmit,
}: UseHealthcareEnrollmentWizardFormParams) => {
  const showEmployeeAddressFields = hasMissingEmployeeAddressData(initialPersonalInformationData)

  const schema = useMemo<HealthcareEnrollmentWizardSchema>(
    () => ({
      planOverview: buildPlanOverviewSchema(healthcarePlanData),
      personalInformation: buildPersonalInformationSchema(
        identifierTypes,
        showEmployeeAddressFields
      ),
    }),
    [healthcarePlanData, identifierTypes, showEmployeeAddressFields]
  )

  const defaultValues = useMemo(
    () => ({
      planOverview: {
        providerName: healthcarePlanData.providerName,
      },
      personalInformation: mapDefaultPersonalInformationValues(initialPersonalInformationData),
    }),
    [healthcarePlanData.providerName, initialPersonalInformationData]
  )

  const steps: F0WizardFormStep[] = [
    {
      title: i18n.t('benefits.healthcare.enrollment_wizard.plan_overview_step.side_title'),
      sectionIds: ['planOverview'],
      nextLabel: i18n.t('common.continue'),
    },
    {
      title: i18n.t('benefits.healthcare.enrollment_wizard.personal_information_step.title'),
      sectionIds: ['personalInformation'],
      nextLabel: i18n.t('common.save'),
    },
  ]

  const handleSubmit = async ({
    sectionId,
    fullData,
  }: F0WizardFormPerSectionSubmitArg<HealthcareEnrollmentWizardSchema>): Promise<F0FormSubmitResult> => {
    if (sectionId !== 'personalInformation') {
      return { success: true }
    }

    try {
      await onSubmit(mapSubmissionData(fullData.personalInformation, showEmployeeAddressFields))

      return { success: true }
    } catch {
      return { success: false }
    }
  }

  const formDefinition = useF0FormDefinition({
    name: 'healthcare-enrollment-wizard',
    schema,
    sections: {
      planOverview: {
        title: '',
        withInset: false,
      },
      personalInformation: {
        title: '',
        withInset: false,
      },
    },
    defaultValues,
    onSubmit: handleSubmit,
    errorTriggerMode: 'on-blur',
  })

  return { formDefinition, steps }
}
